/**
 * Maps interface names (as declared in services.config.yml `interface:` field)
 * to the set of method names that must exist on the implementing instance.
 *
 * This enables structural runtime validation in ServiceLoader: after a service
 * is instantiated, the loader checks that all listed methods are present.
 * TypeScript interfaces are erased at runtime, so this map is the source of truth
 * for the contract at runtime.
 *
 * Keep this in sync with the actual TypeScript interface declarations.
 */
import debugLogger from "../utils/debugLogger";

export const INTERFACE_CONTRACT_MAP: Record<string, readonly string[]> = {
  IAuthApi: ["login", "logout", "fetchCurrentUser"],
  IOrganizationApi: [
    "getOrganization",
    "createOrganization",
    "getOrganizations",
    "updateOrganization",
    "deleteOrganization",
  ],
  IProjectApi: [
    "getProject",
    "getProjects",
    "createProject",
    "updateProject",
    "deleteProject",
    "getProjectApplications",
    "getQuickStartApplications",
  ],
  IUserApi: [
    "getUser",
    "getUserProfile",
    "updateUser",
    "updateUserProfile",
    "getTokens",
    "createToken",
    "updateToken",
    "deleteToken",
    "getPreferences",
    "updatePreferences",
  ],
  IApplicationApi: [
    "getApplication",
    "getApplications",
    "createApplication",
    "updateApplication",
    "deleteApplication",
    "getApplicationStatus",
    "deployApplication",
    "connectToStatusWebSocket",
  ],
};

/**
 * Validate that an instance satisfies the named interface contract.
 * Throws if any required method is missing.
 */
export function validateInterfaceContract(
  serviceName: string,
  interfaceName: string,
  instance: unknown,
): void {
  const requiredMethods = INTERFACE_CONTRACT_MAP[interfaceName];
  if (!requiredMethods) {
    // Unknown interface — warn but don't fail (forward-compatibility)
    debugLogger.warn(
      `[ServiceLoader] Interface "${interfaceName}" for service "${serviceName}" is not registered in INTERFACE_CONTRACT_MAP. Skipping validation.`,
    );
    return;
  }

  const missing = requiredMethods.filter(
    (method) =>
      typeof (instance as Record<string, unknown>)[method] !== "function",
  );

  if (missing.length > 0) {
    throw new Error(
      `Service "${serviceName}" does not implement interface "${interfaceName}". ` +
        `Missing methods: ${missing.join(", ")}`,
    );
  }
}
